"use client";

import { useState, useEffect, useCallback, useMemo, Fragment, type CSSProperties } from "react";
import {
  fetchAdminUsers,
  fetchAdminInvites,
  createAdminInvite,
  revokeAdminInvite,
  resendAdminInvite,
  purgeAdminUser,
  disableAdminUser,
  enableAdminUser,
  fetchAdminUsageSummary,
  fetchAdminUserUsage,
  fetchAdminNotifications,
  markAdminNotificationsRead,
  fetchAdminFeedback,
  fetchAdminDismissedIds,
  addAdminDismissedIds,
  fetchPricingProposals,
  fetchAdminPricing,
  applyPricingProposal,
  rejectPricingProposal,
  fetchModelProposals,
  dismissModelProposal,
} from "@/app/lib/api";
import type {
  AdminBadgeCounts,
  PricingProposal,
  PricingProposalItem,
  ModelProposal,
} from "@/app/lib/types";
import {
  formatBadgeCount,
  BADGE_PILL_STYLE,
} from "@/app/lib/hooks/useAdminBadges";
import ConfirmDialog from "@/app/components/common/ConfirmDialog";
import UsageSections, {
  buildSectionsFromSummary,
  buildSectionsFromUserMonth,
} from "./UsageSections";
import type { PricingDisplayRow } from "@/app/lib/api";

// =============================================================================
// Helpers
// =============================================================================

function formatCost(value: number | undefined | null): string {
  if (value === undefined || value === null) return "$0.00";
  return `$${Number(value).toFixed(2)}`;
}

function formatDate(iso: string | undefined | null): string {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function StatusBadge({
  status,
  colorMap,
}: {
  status: string;
  colorMap: Record<string, string>;
}) {
  const bg = colorMap[status] || "#555";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "10px",
        fontSize: "11px",
        fontWeight: 600,
        background: bg,
        color: "#fff",
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

// =============================================================================
// Admin Sub-Tab type
// =============================================================================

type AdminSubTab = "invites" | "feedback" | "usage" | "pricing" | "alerts";

// Keep in sync with server/pricing.py + server/providers/registry.py.
const PROVIDER_ORDER = ["anthropic", "gemini", "openai"] as const;

function modelLabel(provider: string, mode: string): string {
  const key = `${provider}|${mode}`;
  const labels: Record<string, string> = {
    "anthropic|fast": "Claude Haiku 4.5",
    "anthropic|thinking": "Claude Opus 4.7 (think)",
    "anthropic|pro": "Claude Opus 4.7 (pro)",
    "anthropic|deep_research": "Claude Opus 4.7 (deep)",
    "openai|fast": "GPT-5.5 (fast)",
    "openai|thinking": "GPT-5.5 (thinking)",
    "openai|pro": "GPT-5.5 Pro",
    "openai|deep_research": "OpenAI o3",
    "openai|tts": "OpenAI tts-1",
    "gemini|fast": "Gemini Flash 3 (fast)",
    "gemini|thinking": "Gemini Pro 3.1 (think)",
    "gemini|pro": "Gemini Pro 3.1",
    "gemini|deep_research": "Gemini Pro 3.1 (deep)",
  };
  return labels[key] || `${provider} ${mode}`;
}

/** Label for a per-model breakdown row. Prefers the raw model id from the
 * server's `by_model` (most specific); falls back to the friendly
 * (provider, mode) label for pre-overhaul buckets with no model id. */
function modelLabelFor(provider: string, mode: string, model: string): string {
  return model && model.trim() ? model : modelLabel(provider, mode);
}

function providerDotColor(provider: string): string {
  switch (provider) {
    case "anthropic": return "#cc6144";
    case "gemini": return "#6b5bff";
    case "openai": return "#14a37f";
    default: return "#888";
  }
}

// =============================================================================
// Shared table styles
// =============================================================================

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "12px",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "6px 8px",
  borderBottom: "1px solid var(--border)",
  color: "var(--text-tertiary)",
  fontWeight: 600,
  fontSize: "11px",
};

const thRightStyle: React.CSSProperties = { ...thStyle, textAlign: "right" };

const tdStyle: React.CSSProperties = {
  padding: "6px 8px",
  borderBottom: "1px solid var(--border)",
  color: "var(--text-primary)",
};

const tdRightStyle: React.CSSProperties = { ...tdStyle, textAlign: "right" };

const smallBtnStyle: React.CSSProperties = {
  padding: "3px 10px",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
  background: "transparent",
  color: "var(--text-secondary)",
  fontSize: "11px",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

// =============================================================================
// (Removed) Cost Dashboard — merged into AdminUsageOverview below.
// =============================================================================

// =============================================================================
// Per-model breakdown (used inside Users section expansion)
// =============================================================================

// <select> styling for the shared Usage-tab month picker. Mirrors the
// Settings → Usage page picker so the two read identically.
const adminMonthSelectStyle: CSSProperties = {
  padding: "6px 26px 6px 10px",
  background: "#141414",
  color: "var(--text-primary)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
  fontSize: "12px",
  outline: "none",
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  cursor: "pointer",
  boxShadow: "none",
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 8px center",
};

// Sum every (provider, mode) cell across ALL months of a per-user usage
// map into one synthetic month — backs the "All time" pick in the
// Users-tab month picker.
function aggregateAllMonths(
  usage: Record<string, Record<string, Record<string, any>>>
): Record<string, Record<string, any>> {
  const agg: Record<string, Record<string, any>> = {};
  for (const month of Object.keys(usage)) {
    const byProvider = usage[month] || {};
    for (const provider of Object.keys(byProvider)) {
      const byMode = byProvider[provider] || {};
      for (const mode of Object.keys(byMode)) {
        const s = byMode[mode] || {};
        agg[provider] = agg[provider] || {};
        const cur = agg[provider][mode] || {
          requests: 0, input_tokens: 0, output_tokens: 0, cost_usd: 0,
        };
        cur.requests += s.requests ?? 0;
        cur.input_tokens += s.input_tokens ?? 0;
        cur.output_tokens += s.output_tokens ?? 0;
        cur.cost_usd += s.cost_usd ?? s.cost ?? 0;
        agg[provider][mode] = cur;
      }
    }
  }
  return agg;
}

// Narrow a per-user usage map to the single month chosen by the
// Users-tab picker. `PerModeBreakdown` then renders exactly that one
// month. An empty object means the user had no activity that month.
function scopeUserUsage(
  usage: Record<string, Record<string, Record<string, any>>>,
  month: string
): Record<string, Record<string, Record<string, any>>> {
  if (month === ADMIN_ALL_TIME_KEY) {
    return { [ADMIN_ALL_TIME_LABEL]: aggregateAllMonths(usage) };
  }
  return usage[month] ? { [month]: usage[month] } : {};
}

// Per-user usage expander. `usage` is the per-user map narrowed to one
// scope ({month: {provider: {mode: {...by_model{}}}}}) — typically a
// single month. Each month renders a small header + the same collapsible
// section list the user sees in their own Usage panel.
function PerModeBreakdown({
  usage,
}: {
  usage: Record<string, Record<string, Record<string, any>>>;
}) {
  const months = Object.keys(usage).sort().reverse();
  if (months.length === 0) {
    return (
      <div style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>
        No usage data available.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {months.map((month) => {
        const byProvider = usage[month] || {};
        // Month header totals — sum every (provider, mode) cell. Audio
        // (TTS) auto-fires from voice mode (~19 calls per answer), so it
        // is excluded from the request count; cost stays inclusive.
        let monthTotalCost = 0;
        let monthTotalReqs = 0;
        for (const provider of Object.keys(byProvider)) {
          const modes = byProvider[provider] || {};
          for (const mode of Object.keys(modes)) {
            const stats = modes[mode];
            if (!stats || typeof stats !== "object") continue;
            const requests = stats.requests ?? 0;
            const cost = stats.cost_usd ?? stats.cost ?? 0;
            monthTotalCost += cost;
            if (mode !== "audio" && mode !== "tts") monthTotalReqs += requests;
          }
        }

        const sections = buildSectionsFromUserMonth(byProvider, modelLabelFor);

        return (
          <div key={month}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "11px",
                color: "var(--text-tertiary)",
                fontWeight: 600,
                marginBottom: "6px",
              }}
            >
              <span>{month}</span>
              <span style={{ fontFamily: "ui-monospace, monospace" }}>
                {monthTotalReqs} reqs · {formatCost(monthTotalCost)}
              </span>
            </div>
            <UsageSections sections={sections} compact />
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// Users Section (rendered below the admin-wide overview in the Usage sub-tab)
// =============================================================================

// Per-row Requests/Cost for the user table — reflects the shared month
// picker. For a specific month, read straight from the server-provided
// `usage_by_month` map; for "All time", sum across every month.
function userRowUsage(
  user: any,
  selectedMonth: string
): { requests: number; cost: number } {
  const byMonth: Record<string, { requests?: number; cost_usd?: number }> =
    user?.usage_by_month || {};
  if (selectedMonth === ADMIN_ALL_TIME_KEY) {
    let requests = 0;
    let cost = 0;
    for (const m of Object.keys(byMonth)) {
      const s = byMonth[m] || {};
      requests += s.requests ?? 0;
      cost += s.cost_usd ?? 0;
    }
    return { requests, cost };
  }
  const s = byMonth[selectedMonth];
  return { requests: s?.requests ?? 0, cost: s?.cost_usd ?? 0 };
}

function UsersSection({ selectedMonth }: { selectedMonth: string }) {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEmail, setExpandedEmail] = useState<string | null>(null);
  const [userUsage, setUserUsage] = useState<any>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAdminUsers();
      setUsers(Array.isArray(data) ? data : data.users || []);
    } catch (e: any) {
      setError(e.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleExpandUser = useCallback(
    async (email: string) => {
      if (expandedEmail === email) {
        setExpandedEmail(null);
        setUserUsage(null);
        return;
      }
      setExpandedEmail(email);
      setUsageLoading(true);
      try {
        const usage = await fetchAdminUserUsage(email);
        setUserUsage(usage);
      } catch {
        setUserUsage(null);
      } finally {
        setUsageLoading(false);
      }
    },
    [expandedEmail]
  );

  if (loading) return <div style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>Loading...</div>;
  if (error) return <div style={{ color: "#e55", fontSize: "12px" }}>{error}</div>;

  const statusColors: Record<string, string> = {
    active: "#2ea043",
    disabled: "#da3633",
    pending_deletion: "#d29922",
  };

  // Sort users: month-scoped cost DESCENDING primary, last-name alphabetical
  // tiebreaker (handles the everyone-tied-at-zero case when no usage has
  // been recorded for the selected month). No pinned-top ranks here —
  // Users is cost-focused; Invites carries the pinned 4-canonical-account
  // order instead. Confirmed by admin 2026-05-25.
  const sortedUsers = [...users].sort((a: any, b: any) => {
    const aCost = userRowUsage(a, selectedMonth).cost;
    const bCost = userRowUsage(b, selectedMonth).cost;
    if (aCost !== bCost) return bCost - aCost;
    return lastNameSortKey(a.name, a.email).localeCompare(
      lastNameSortKey(b.name, b.email),
    );
  });

  // "Users (x)" heading — x counts only active users.
  const activeUserCount = users.filter(
    (u: any) => (u.status || "active") === "active"
  ).length;

  return (
    <div className="admin-users-wide" style={{ overflowX: "auto" }}>
      {/* No month picker here — the user table (rows + expanders) is
          scoped by the single shared picker at the top of the Usage
          sub-tab, passed in via `selectedMonth`. */}
      <div
        style={{
          fontSize: "13px",
          fontWeight: 600,
          color: "var(--text-secondary)",
          marginBottom: "8px",
        }}
      >
        Users ({activeUserCount})
      </div>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Name</th>
            <th style={thStyle}>Email</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Created</th>
            <th style={thStyle}>Last Active</th>
            <th style={thRightStyle}>Requests</th>
            <th style={thRightStyle}>Cost</th>
          </tr>
        </thead>
        <tbody>
          {sortedUsers.map((user: any) => {
            const email = user.email || user.id;
            const status = user.status || "active";
            const isExpanded = expandedEmail === email;
            // Per-row Requests/Cost reflect the shared month picker —
            // re-derived on every render so changing the picker re-filters
            // every row.
            const rowUsage = userRowUsage(user, selectedMonth);
            return (
              <Fragment key={email}>
                <tr
                  onClick={() => handleExpandUser(email)}
                  style={{ cursor: "pointer" }}
                >
                  <td style={tdStyle}>{user.name || "—"}</td>
                  <td style={tdStyle}>{email}</td>
                  <td style={tdStyle}>
                    <StatusBadge status={status} colorMap={statusColors} />
                  </td>
                  <td style={tdStyle}>{formatDate(user.created_at)}</td>
                  <td style={tdStyle}>{formatDate(user.last_active)}</td>
                  <td style={tdRightStyle}>{rowUsage.requests}</td>
                  <td style={tdRightStyle}>{formatCost(rowUsage.cost)}</td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={7} style={{ padding: "8px 8px 8px 24px", borderBottom: "1px solid var(--border)" }}>
                      {usageLoading ? (
                        <div style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>Loading usage...</div>
                      ) : userUsage ? (
                        (() => {
                          const scoped = scopeUserUsage(userUsage.usage || {}, selectedMonth);
                          return Object.keys(scoped).length === 0 ? (
                            <div style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>
                              No usage in {selectedMonth}.
                            </div>
                          ) : (
                            <PerModeBreakdown usage={scoped} />
                          );
                        })()
                      ) : (
                        <div style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>No usage data available.</div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {users.length === 0 && (
        <div style={{ padding: "12px 0", textAlign: "center", color: "var(--text-tertiary)", fontSize: "12px" }}>
          No users found.
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Display-order helpers — shared between InvitesSection and UsersSection.
// Keep in sync with iOS AdminConsoleView.swift.
//
// `lastNameSortKey`        — alpha by last name (email fallback when name blank).
//                            Used directly by Users (cost-DESC primary, last-name tiebreaker).
// `displayOrderKey`        — pinned 4 canonical accounts first, then last-name.
//                            Used by Invites.
// =============================================================================

function lastNameSortKey(
  name: string | null | undefined,
  email: string,
): string {
  const trimmedName = (name || "").trim();
  if (!trimmedName) {
    return (email || "").toLowerCase();
  }
  const tokens = trimmedName.split(/\s+/);
  const lastName = tokens[tokens.length - 1] || trimmedName;
  return lastName.toLowerCase();
}

function displayOrderKey(
  name: string | null | undefined,
  email: string,
): { rank: number; sortKey: string } {
  const lowerEmail = (email || "").toLowerCase();
  let pinnedRank = 4;
  switch (lowerEmail) {
    case "ayknyc@gmail.com": pinnedRank = 0; break;       // Tony Kim
    case "testrunner@test.com": pinnedRank = 1; break;    // Test Runner
    case "appreview@trioai.co": pinnedRank = 2; break;    // App Review
    case "friend@trioai.co": pinnedRank = 3; break;       // Trio Friend
  }
  if (pinnedRank < 4) {
    return { rank: pinnedRank, sortKey: "" };
  }
  return { rank: 4, sortKey: lastNameSortKey(name, email) };
}

function compareDisplayOrder(
  a: { name?: string | null; email: string },
  b: { name?: string | null; email: string },
): number {
  const ka = displayOrderKey(a.name, a.email);
  const kb = displayOrderKey(b.name, b.email);
  if (ka.rank !== kb.rank) return ka.rank - kb.rank;
  return ka.sortKey.localeCompare(kb.sortKey);
}

// =============================================================================
// Invites Section
// =============================================================================

function InvitesSection() {
  const [invites, setInvites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [sending, setSending] = useState(false);
  // Which row's Actions menu is open (code-or-email key). Click anywhere
  // else / pick an action → closes.
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);

  const loadInvites = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAdminInvites();
      setInvites(Array.isArray(data) ? data : data.invites || []);
    } catch (e: any) {
      setError(e.message || "Failed to load invites");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  const handleCreate = useCallback(
    async (sendToSelf: boolean) => {
      if (!newName.trim() || !newEmail.trim()) return;
      try {
        setSending(true);
        setError(null);
        await createAdminInvite(newEmail.trim(), newName.trim(), sendToSelf);
        setNewName("");
        setNewEmail("");
        await loadInvites();
      } catch (e: any) {
        setError(e.message || "Failed to create invite");
      } finally {
        setSending(false);
      }
    },
    [newName, newEmail, loadInvites],
  );

  const handleRevoke = useCallback(
    async (code: string) => {
      try {
        setError(null);
        await revokeAdminInvite(code);
        await loadInvites();
      } catch (e: any) {
        setError(e.message || "Failed to revoke invite");
      }
    },
    [loadInvites]
  );

  const handleResend = useCallback(
    async (code: string, sendToSelf: boolean) => {
      try {
        setError(null);
        await resendAdminInvite(code, sendToSelf);
        // No list refresh needed — resend doesn't change invite state.
      } catch (e: any) {
        setError(e.message || "Failed to resend invite");
      }
    },
    [],
  );

  const handlePurge = useCallback(
    async (email: string) => {
      // Two-step confirmation — destructive, bypasses 30-day grace.
      const ok = window.confirm(
        `Permanently delete ${email} and all their data?\n\nThis bypasses the 30-day grace period. They will be able to register again with the same email.`,
      );
      if (!ok) return;
      try {
        setError(null);
        await purgeAdminUser(email);
        await loadInvites();
      } catch (e: any) {
        setError(e.message || "Failed to delete user");
      }
    },
    [loadInvites],
  );

  // Disable/Enable the registered user behind an invite row — wired to the
  // same admin API the user table previously owned (moved here in wave 2).
  const handleToggleStatus = useCallback(
    async (email: string, currentStatus: string) => {
      try {
        setError(null);
        if (currentStatus === "disabled") {
          await enableAdminUser(email);
        } else {
          await disableAdminUser(email);
        }
        await loadInvites();
      } catch (e: any) {
        setError(e.message || "Failed to update user");
      }
    },
    [loadInvites]
  );

  const statusColors: Record<string, string> = {
    active: "#2ea043",
    used: "#388bfd",
    expired: "#555",
    revoked: "#da3633",
  };

  const sectionHeaderStyle: React.CSSProperties = {
    fontSize: "11px",
    color: "var(--text-tertiary)",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* New invitations */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={sectionHeaderStyle}>New invitations</div>
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Name"
          maxLength={100}
          onKeyDown={(e) => e.key === "Enter" && handleCreate(false)}
          style={{
            padding: "6px 10px",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            background: "var(--bg)",
            color: "var(--text-primary)",
            fontSize: "12px",
            outline: "none",
          }}
        />
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="Email address"
          onKeyDown={(e) => e.key === "Enter" && handleCreate(false)}
          style={{
            padding: "6px 10px",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            background: "var(--bg)",
            color: "var(--text-primary)",
            fontSize: "12px",
            outline: "none",
          }}
        />
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => handleCreate(false)}
            disabled={sending || !newName.trim() || !newEmail.trim()}
            style={{
              flex: 1,
              padding: "8px 14px",
              border: "none",
              borderRadius: "var(--radius-sm)",
              background: "var(--accent)",
              color: "#fff",
              fontSize: "12px",
              fontWeight: 600,
              cursor:
                sending || !newName.trim() || !newEmail.trim()
                  ? "default"
                  : "pointer",
              opacity:
                sending || !newName.trim() || !newEmail.trim() ? 0.5 : 1,
            }}
          >
            {sending ? "Sending..." : "Send"}
          </button>
          <button
            onClick={() => handleCreate(true)}
            disabled={sending || !newName.trim() || !newEmail.trim()}
            title="Send the invite email to yourself instead of the recipient — handy when you want to forward it personally or you've been getting filtered as spam"
            style={{
              flex: 1,
              padding: "8px 14px",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              background: "transparent",
              color: "var(--text-primary)",
              fontSize: "12px",
              fontWeight: 600,
              cursor:
                sending || !newName.trim() || !newEmail.trim()
                  ? "default"
                  : "pointer",
              opacity:
                sending || !newName.trim() || !newEmail.trim() ? 0.5 : 1,
            }}
          >
            Send to Me
          </button>
        </div>
        {error && <div style={{ color: "#e55", fontSize: "12px" }}>{error}</div>}
      </div>

      {/* Previous invitations */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={sectionHeaderStyle}>Previous invitations</div>
        {loading ? (
          <div style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>Loading...</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Created</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv: any) => {
                const status = inv.status || "active";
                // Disable/Enable shows only for a registered, non-admin user.
                // `code: null` rows are synthetic (registered user, no real
                // invite) — they get the toggle but not the Revoke affordance.
                const showToggle =
                  inv.user_registered === true && inv.is_admin !== true;
                const userStatus: string = inv.user_status || "active";
                return (
                  <tr key={inv.code || inv.id || inv.email}>
                    <td style={tdStyle}>{inv.name || "—"}</td>
                    <td style={tdStyle}>{inv.email || "-"}</td>
                    <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: "11px" }}>
                      {inv.code || "-"}
                    </td>
                    <td style={tdStyle}>
                      <StatusBadge status={status} colorMap={statusColors} />
                    </td>
                    <td style={tdStyle}>{formatDate(inv.created_at)}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      {(() => {
                        const rowKey = inv.code || inv.email || "";
                        const isOpen = openMenuFor === rowKey;
                        const menuItemStyle: React.CSSProperties = {
                          display: "block",
                          width: "100%",
                          textAlign: "left",
                          padding: "6px 12px",
                          border: "none",
                          background: "transparent",
                          color: "var(--text-primary)",
                          fontSize: "12px",
                          cursor: "pointer",
                        };
                        const dangerItemStyle: React.CSSProperties = {
                          ...menuItemStyle,
                          color: "#da3633",
                        };
                        const dividerStyle: React.CSSProperties = {
                          height: "1px",
                          background: "var(--border)",
                          margin: "4px 0",
                        };
                        const hasAnyAction =
                          (inv.code && true) ||
                          showToggle ||
                          (status === "active" && inv.code);
                        if (!hasAnyAction) return null;
                        return (
                          <div style={{ position: "relative", display: "inline-block" }}>
                            <button
                              style={smallBtnStyle}
                              onClick={() => setOpenMenuFor(isOpen ? null : rowKey)}
                            >
                              Actions ▾
                            </button>
                            {isOpen && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  right: 0,
                                  marginTop: "2px",
                                  background: "var(--bg)",
                                  border: "1px solid var(--border)",
                                  borderRadius: "var(--radius-sm)",
                                  padding: "4px 0",
                                  minWidth: "180px",
                                  zIndex: 10,
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                                }}
                                onMouseLeave={() => setOpenMenuFor(null)}
                              >
                                {inv.code && (
                                  <>
                                    <button
                                      style={menuItemStyle}
                                      onClick={() => {
                                        handleResend(inv.code, false);
                                        setOpenMenuFor(null);
                                      }}
                                    >
                                      Resend
                                    </button>
                                    <button
                                      style={menuItemStyle}
                                      onClick={() => {
                                        handleResend(inv.code, true);
                                        setOpenMenuFor(null);
                                      }}
                                    >
                                      Resend to Me
                                    </button>
                                  </>
                                )}
                                {showToggle && (
                                  <button
                                    style={menuItemStyle}
                                    onClick={() => {
                                      handleToggleStatus(inv.email, userStatus);
                                      setOpenMenuFor(null);
                                    }}
                                  >
                                    {userStatus === "disabled" ? "Enable account" : "Disable account"}
                                  </button>
                                )}
                                {status === "active" && inv.code && (
                                  <button
                                    style={dangerItemStyle}
                                    onClick={() => {
                                      handleRevoke(inv.code);
                                      setOpenMenuFor(null);
                                    }}
                                  >
                                    Revoke invite
                                  </button>
                                )}
                                {showToggle && (
                                  <>
                                    <div style={dividerStyle} />
                                    <button
                                      style={dangerItemStyle}
                                      onClick={() => {
                                        handlePurge(inv.email);
                                        setOpenMenuFor(null);
                                      }}
                                    >
                                      Delete account…
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
            {invites.length === 0 && (
              <div style={{ padding: "12px 0", textAlign: "center", color: "var(--text-tertiary)", fontSize: "12px" }}>
                No invites found.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Pending Pricing Proposals (rendered at top of Usage sub-tab)
// =============================================================================

const CONFIDENCE_COLOR: Record<string, string> = {
  high: "#14a37f",
  medium: "#d4a017",
  low: "#c94040",
};

function ConfidenceChip({ confidence }: { confidence: string }) {
  const bg = CONFIDENCE_COLOR[confidence] || "#555";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 7px",
        borderRadius: "10px",
        fontSize: "10px",
        fontWeight: 700,
        background: bg,
        color: "#fff",
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        whiteSpace: "nowrap",
      }}
    >
      {confidence}
    </span>
  );
}

function formatRateUsd(n: number): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return "-";
  return `$${v.toFixed(2)}`;
}

function itemKey(it: { provider: string; mode: string }): string {
  return `${it.provider}|${it.mode}`;
}

function defaultApprovedKeys(proposal: PricingProposal): Set<string> {
  const set = new Set<string>();
  for (const it of proposal.items) {
    if (it.confidence === "high" || it.confidence === "medium") {
      set.add(itemKey(it));
    }
  }
  return set;
}

function PricingRateDiff({
  label,
  current,
  proposed,
}: {
  label: string;
  current: number;
  proposed: number;
}) {
  const increased = proposed > current;
  const decreased = proposed < current;
  const color = increased ? "#c94040" : decreased ? "#14a37f" : "var(--text-primary)";
  return (
    <div style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
      <span style={{ color: "var(--text-tertiary)", marginRight: "4px" }}>{label}</span>
      <span style={{ fontFamily: "ui-monospace, monospace" }}>
        {formatRateUsd(current)} <span style={{ color: "var(--text-tertiary)" }}>-&gt;</span>{" "}
        <span style={{ color, fontWeight: 600 }}>{formatRateUsd(proposed)}</span>
      </span>
    </div>
  );
}

function ProposalCard({
  proposal,
  onApplied,
  onRejected,
}: {
  proposal: PricingProposal;
  onApplied: () => void;
  onRejected: () => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const [approvedKeys, setApprovedKeys] = useState<Set<string>>(
    () => defaultApprovedKeys(proposal)
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);

  const toggleItem = useCallback((key: string) => {
    setApprovedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleApply = useCallback(async () => {
    if (approvedKeys.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const approved = proposal.items
        .filter((it) => approvedKeys.has(itemKey(it)))
        .map((it) => ({ provider: it.provider, mode: it.mode }));
      await applyPricingProposal(proposal.id, approved);
      onApplied();
    } catch (e: any) {
      setError(e?.message || "Failed to apply proposal");
    } finally {
      setBusy(false);
    }
  }, [approvedKeys, proposal, onApplied]);

  const priorAppliedCount = proposal.applied_items?.length ?? 0;
  const remainingCount = proposal.items.length;
  const isPartial = priorAppliedCount > 0;
  const rejectButtonLabel = isPartial ? "Reject Remaining" : "Reject All";
  const rejectDialogTitle = isPartial ? "Reject remaining items?" : "Reject proposal?";
  const rejectDialogMessage = isPartial
    ? `Reject the remaining ${remainingCount} item${remainingCount === 1 ? "" : "s"}? Previously applied changes (${priorAppliedCount}) stay in effect.`
    : "This will reject the entire proposal. No pricing changes will be applied.";

  const handleRejectConfirmed = useCallback(async () => {
    setRejectConfirmOpen(false);
    setBusy(true);
    setError(null);
    try {
      await rejectPricingProposal(proposal.id);
      onRejected();
    } catch (e: any) {
      setError(e?.message || "Failed to reject proposal");
    } finally {
      setBusy(false);
    }
  }, [proposal, onRejected]);

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        background: "var(--bg)",
        overflow: "hidden",
      }}
    >
      {/* Header row (collapsible) */}
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 12px",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          color: "var(--text-primary)",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <div style={{ fontSize: "12px", fontWeight: 600 }}>
            Proposal {formatDate(proposal.created_at)}
          </div>
          <div style={{ fontSize: "11px", color: "var(--text-tertiary)" }}>
            {proposal.items.length} item{proposal.items.length === 1 ? "" : "s"}
            {priorAppliedCount > 0 && ` — ${priorAppliedCount} already applied`}
          </div>
        </div>
        <span style={{ fontSize: "12px", color: "var(--text-tertiary)" }}>
          {expanded ? "v" : ">"}
        </span>
      </button>

      {expanded && (
        <div style={{ padding: "0 12px 12px 12px", display: "flex", flexDirection: "column", gap: "8px" }}>
          {proposal.items.map((it: PricingProposalItem) => {
            const key = itemKey(it);
            const checked = approvedKeys.has(key);
            const isLow = it.confidence === "low";
            return (
              <div
                key={key}
                style={{
                  display: "flex",
                  gap: "10px",
                  padding: "8px 10px",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--surface)",
                  alignItems: "flex-start",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleItem(key)}
                  style={{ marginTop: "3px", cursor: "pointer" }}
                />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: providerDotColor(it.provider),
                      }}
                    />
                    <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-primary)" }}>
                      {modelLabel(it.provider, it.mode)}
                    </span>
                    <span style={{ fontSize: "11px", color: "var(--text-tertiary)" }}>
                      ({it.provider} / {it.mode})
                    </span>
                    <ConfidenceChip confidence={it.confidence} />
                    {isLow && (
                      <span style={{ fontSize: "10px", color: "#c94040", fontStyle: "italic" }}>
                        low confidence — review carefully
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "18px", flexWrap: "wrap" }}>
                    <PricingRateDiff label="Input" current={it.current_input} proposed={it.proposed_input} />
                    <PricingRateDiff label="Output" current={it.current_output} proposed={it.proposed_output} />
                  </div>
                  {it.reasoning && (
                    <div style={{ fontSize: "11px", color: "var(--text-secondary)", lineHeight: 1.4 }}>
                      {it.reasoning}
                    </div>
                  )}
                  {it.source_url && (
                    <a
                      href={it.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: "11px", color: "#58a6ff", textDecoration: "underline" }}
                    >
                      Source
                    </a>
                  )}
                </div>
              </div>
            );
          })}

          {error && <div style={{ color: "#e55", fontSize: "12px" }}>{error}</div>}

          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "4px" }}>
            <button
              onClick={() => setRejectConfirmOpen(true)}
              disabled={busy}
              style={{
                ...smallBtnStyle,
                color: "#da3633",
                borderColor: "#da3633",
                opacity: busy ? 0.5 : 1,
                cursor: busy ? "default" : "pointer",
              }}
            >
              {rejectButtonLabel}
            </button>
            <button
              onClick={handleApply}
              disabled={busy || approvedKeys.size === 0}
              style={{
                padding: "3px 12px",
                border: "none",
                borderRadius: "var(--radius-sm)",
                background: "var(--accent)",
                color: "#fff",
                fontSize: "11px",
                fontWeight: 600,
                cursor: busy || approvedKeys.size === 0 ? "default" : "pointer",
                opacity: busy || approvedKeys.size === 0 ? 0.5 : 1,
                whiteSpace: "nowrap",
              }}
            >
              {busy ? "Working..." : `Apply Selected (${approvedKeys.size})`}
            </button>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={rejectConfirmOpen}
        title={rejectDialogTitle}
        message={rejectDialogMessage}
        confirmLabel={rejectButtonLabel}
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleRejectConfirmed}
        onCancel={() => setRejectConfirmOpen(false)}
      />
    </div>
  );
}

function PendingPricingProposals({ onChanged }: { onChanged?: () => void }) {
  const [proposals, setProposals] = useState<PricingProposal[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await fetchPricingProposals();
      setProposals(Array.isArray(data.proposals) ? data.proposals : []);
    } catch (e: any) {
      setError(e?.message || "Failed to load pricing proposals");
      setProposals([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleChanged = useCallback(() => {
    load();
    if (onChanged) onChanged();
  }, [load, onChanged]);

  // Hide section entirely when list is empty (also on initial null, on error).
  if (error) return <div style={{ color: "#e55", fontSize: "12px" }}>{error}</div>;
  if (!proposals || proposals.length === 0) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <div style={{ fontSize: "11px", color: "var(--text-tertiary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px" }}>
        Pending Pricing Proposals
      </div>
      {proposals.map((p) => (
        <ProposalCard
          key={p.id}
          proposal={p}
          onApplied={handleChanged}
          onRejected={handleChanged}
        />
      ))}
    </div>
  );
}

function PendingModelProposals({ onChanged }: { onChanged?: () => void }) {
  const [proposals, setProposals] = useState<ModelProposal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await fetchModelProposals();
      setProposals(Array.isArray(data.proposals) ? data.proposals : []);
    } catch (e: any) {
      setError(e?.message || "Failed to load model proposals");
      setProposals([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleDismiss = useCallback(async (id: string) => {
    setBusyId(id);
    try {
      await dismissModelProposal(id);
      await load();
      if (onChanged) onChanged();
    } catch (e: any) {
      setError(e?.message || "Failed to dismiss proposal");
    } finally {
      setBusyId(null);
    }
  }, [load, onChanged]);

  if (error) return <div style={{ color: "#e55", fontSize: "12px" }}>{error}</div>;
  if (!proposals || proposals.length === 0) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <div style={{ fontSize: "11px", color: "var(--text-tertiary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px" }}>
        New Model Alerts
      </div>
      {proposals.map((p) => {
        const isExpanded = expanded.has(p.id);
        const isBusy = busyId === p.id;
        return (
          <div
            key={p.id}
            style={{
              background: "var(--bg)",
              borderRadius: "var(--radius-md)",
              padding: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <button
              type="button"
              onClick={() => toggleExpanded(p.id)}
              style={{
                background: "transparent", border: "none", padding: 0,
                display: "flex", alignItems: "center", gap: "8px",
                cursor: "pointer", color: "var(--text-primary)",
                textAlign: "left",
              }}
            >
              <span style={{ fontSize: "16px" }}>✨</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "13px", fontWeight: 600 }}>
                  {p.items.length} new model{p.items.length === 1 ? "" : "s"} detected
                </div>
                {p.created_at && (
                  <div style={{ fontSize: "11px", color: "var(--text-tertiary)" }}>
                    {p.created_at.slice(0, 10)}
                  </div>
                )}
              </div>
              <span style={{ fontSize: "11px", color: "var(--text-tertiary)" }}>
                {isExpanded ? "▲" : "▼"}
              </span>
            </button>

            {isExpanded && (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingLeft: "24px" }}>
                {[...p.items].sort((a, b) => {
                  // High-priority upgrades first, then medium (alias /
                  // new variant), then low (sidegrades / errors). Stable
                  // within bucket by (provider, model_id).
                  const order: Record<string, number> = { high: 0, medium: 1, low: 2 };
                  const ap = order[a.upgrade_priority || "low"] ?? 9;
                  const bp = order[b.upgrade_priority || "low"] ?? 9;
                  if (ap !== bp) return ap - bp;
                  if (a.provider !== b.provider) return a.provider.localeCompare(b.provider);
                  return a.model_id.localeCompare(b.model_id);
                }).map((item) => {
                  // Per-priority badge colors. Subtle — picks up against
                  // the row's normal text without screaming.
                  const priColor =
                    item.upgrade_priority === "high"
                      ? { bg: "rgba(56, 139, 253, 0.18)", fg: "#58a6ff" }
                      : item.upgrade_priority === "low"
                      ? { bg: "rgba(155, 155, 155, 0.18)", fg: "#9aa0a6" }
                      : { bg: "rgba(255, 184, 0, 0.16)",  fg: "#f5b400" };
                  return (
                  <div
                    key={`${item.provider}/${item.model_id}`}
                    style={{
                      display: "flex", flexDirection: "column", gap: "4px",
                      paddingBottom: "8px",
                      borderBottom: "1px solid var(--border-subtle, rgba(0,0,0,0.06))",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "baseline", gap: "6px", flexWrap: "wrap" }}>
                      {item.upgrade_priority && (
                        <span style={{
                          fontSize: "10px", fontWeight: 700,
                          padding: "2px 6px",
                          background: priColor.bg,
                          color: priColor.fg,
                          borderRadius: "9999px",
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}>
                          {item.upgrade_priority}
                        </span>
                      )}
                      <span style={{ fontSize: "13px", fontWeight: 600 }}>
                        {item.family || item.model_id}
                      </span>
                      <span style={{ fontSize: "11px", color: "var(--text-tertiary)" }}>
                        ({item.provider})
                      </span>
                      {item.category && (
                        <span style={{
                          fontSize: "10px", fontWeight: 600,
                          padding: "2px 6px",
                          background: "var(--bg-secondary, rgba(0,0,0,0.06))",
                          borderRadius: "9999px",
                        }}>
                          {item.category}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: "11px", fontFamily: "monospace", color: "var(--text-secondary)" }}>
                      {item.model_id}
                    </div>
                    {item.current_models && (
                      <div style={{ fontSize: "11px", color: "var(--text-tertiary)" }}>
                        Trio uses now: {item.current_models}
                      </div>
                    )}
                    {item.upgrade_reason && (
                      <div style={{ fontSize: "12px", color: "var(--text-primary)", marginTop: "2px" }}>
                        {item.upgrade_reason}
                      </div>
                    )}
                  </div>
                );})}
                <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "4px" }}>
                  <button
                    type="button"
                    onClick={() => handleDismiss(p.id)}
                    disabled={isBusy}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "var(--radius-sm)",
                      border: "1px solid var(--border-strong, #c33)",
                      background: "transparent",
                      color: "var(--text-primary)",
                      fontSize: "12px",
                      cursor: isBusy ? "not-allowed" : "pointer",
                      opacity: isBusy ? 0.5 : 1,
                    }}
                  >
                    {isBusy ? "Dismissing..." : "Dismiss"}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Sentinel month value for the synthetic "All time" aggregation in
// the admin Usage view. Same key the user-facing UsagePanel uses so the
// shared UX language stays consistent.
const ADMIN_ALL_TIME_KEY = "__all__";
const ADMIN_ALL_TIME_LABEL = "All time";

type AdminUsageByModel = {
  model: string;
  requests: number;
  web_search_count?: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
};
type AdminUsageRow = {
  provider: string;
  mode: string;
  requests: number;
  web_search_count?: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  by_model?: AdminUsageByModel[];
};
type AdminUsageSection = {
  section: string;
  requests: number;
  web_search_count?: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
};
type AdminUsageMonth = {
  month: string;
  total_cost_usd: number;
  total_requests: number;
  total_input_tokens?: number;
  total_output_tokens?: number;
  by_provider?: Record<string, { requests: number; cost_usd: number; input_tokens: number; output_tokens: number }>;
  rows?: AdminUsageRow[];
  sections?: AdminUsageSection[];
};

function buildAdminAllTimeMonth(months: AdminUsageMonth[]): AdminUsageMonth {
  const rowMap = new Map<string, AdminUsageRow>();
  const sectMap = new Map<string, AdminUsageSection>();
  let cost = 0, reqs = 0, tin = 0, tout = 0;
  for (const m of months) {
    cost += m.total_cost_usd || 0;
    reqs += m.total_requests || 0;
    tin += m.total_input_tokens || 0;
    tout += m.total_output_tokens || 0;
    for (const r of m.rows || []) {
      const k = `${r.provider}|${r.mode}`;
      const existing = rowMap.get(k);
      if (existing) {
        existing.requests += r.requests;
        existing.web_search_count = (existing.web_search_count ?? 0) + (r.web_search_count ?? 0);
        existing.input_tokens += r.input_tokens;
        existing.output_tokens += r.output_tokens;
        existing.cost_usd += r.cost_usd;
        // Merge nested per-model rows by model id.
        const byModel = existing.by_model || [];
        for (const bm of r.by_model || []) {
          const found = byModel.find((x) => x.model === bm.model);
          if (found) {
            found.requests += bm.requests;
            found.web_search_count = (found.web_search_count ?? 0) + (bm.web_search_count ?? 0);
            found.input_tokens += bm.input_tokens;
            found.output_tokens += bm.output_tokens;
            found.cost_usd += bm.cost_usd;
          } else {
            byModel.push({ ...bm });
          }
        }
        existing.by_model = byModel;
      } else {
        rowMap.set(k, {
          ...r,
          by_model: (r.by_model || []).map((bm) => ({ ...bm })),
        });
      }
    }
    for (const s of m.sections || []) {
      const existing = sectMap.get(s.section);
      if (existing) {
        existing.requests += s.requests;
        existing.web_search_count = (existing.web_search_count ?? 0) + (s.web_search_count ?? 0);
        existing.input_tokens += s.input_tokens;
        existing.output_tokens += s.output_tokens;
        existing.cost_usd += s.cost_usd;
      } else {
        sectMap.set(s.section, { ...s });
      }
    }
  }
  return {
    month: ADMIN_ALL_TIME_KEY,
    total_cost_usd: cost,
    total_requests: reqs,
    total_input_tokens: tin,
    total_output_tokens: tout,
    rows: Array.from(rowMap.values()),
    sections: Array.from(sectMap.values()),
  };
}

function rollupRowsByProvider(rows: AdminUsageRow[]): Record<string, { requests: number; cost_usd: number }> {
  const acc: Record<string, { requests: number; cost_usd: number }> = {};
  for (const r of rows) {
    if (!acc[r.provider]) acc[r.provider] = { requests: 0, cost_usd: 0 };
    // Audio (TTS) is voice-mode driven (~19 calls per chat answer);
    // exclude its request count from per-provider rollups so the
    // chat-request signal stays visible. Cost stays inclusive.
    if (r.mode !== "audio" && r.mode !== "tts") acc[r.provider].requests += r.requests;
    acc[r.provider].cost_usd += r.cost_usd;
  }
  return acc;
}

// Admin-wide usage overview — totals card / provider-totals strip /
// collapsible per-section breakdown. Renders at the TOP of the Usage
// sub-tab, above the user list. Scoped by the shared `selectedMonth`
// from the parent; it no longer owns a month picker (one shared picker
// drives both this and the user list).
function AdminUsageOverview({
  data,
  loading,
  error,
  selectedMonth,
}: {
  data: { months: AdminUsageMonth[] } | null;
  loading: boolean;
  error: string | null;
  selectedMonth: string;
}) {
  const months: AdminUsageMonth[] = data?.months || [];
  const allTimeEntry: AdminUsageMonth | null = months.length > 0 ? buildAdminAllTimeMonth(months) : null;
  const selected: AdminUsageMonth | undefined =
    selectedMonth === ADMIN_ALL_TIME_KEY
      ? allTimeEntry ?? undefined
      : months.find((m) => m.month === selectedMonth) || months[0];
  const flatRows: AdminUsageRow[] = selected?.rows || [];
  const providerTotals = rollupRowsByProvider(flatRows);
  // Collapsible sections — one per mode, fixed order, collapsed by default.
  const usageSections = buildSectionsFromSummary(
    selected?.rows,
    selected?.sections,
    modelLabelFor
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {loading && (
        <div style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>Loading...</div>
      )}
      {error && <div style={{ color: "#e55", fontSize: "12px" }}>{error}</div>}

      {data && months.length === 0 && (
        <div style={{ color: "var(--text-tertiary)", fontSize: "12px", textAlign: "center", padding: "16px 0" }}>
          No admin-wide usage yet.
        </div>
      )}

      {data && months.length > 0 && (
        <>
          {/* Selected-scope total header */}
          <div
            style={{
              padding: "16px",
              background: "var(--bg)",
              borderRadius: "var(--radius-md)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "11px", color: "var(--text-tertiary)", marginBottom: "4px" }}>
              {selected?.month === ADMIN_ALL_TIME_KEY ? ADMIN_ALL_TIME_LABEL : (selected?.month || "—")}
            </div>
            <div style={{ fontSize: "28px", fontWeight: 700, color: "var(--text-primary)" }}>
              {formatCost(selected?.total_cost_usd)}
            </div>
            <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "4px" }}>
              {selected?.total_requests ?? 0} requests
            </div>
          </div>

          {/* Provider totals strip — three cells, requests + cost per provider */}
          {flatRows.length > 0 && (
            <div style={{ display: "flex", gap: "8px" }}>
              {PROVIDER_ORDER.map((provider) => {
                const t = providerTotals[provider] || { requests: 0, cost_usd: 0 };
                return (
                  <div
                    key={provider}
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      background: "var(--surface-hover)",
                      borderRadius: "var(--radius-md)",
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: "var(--text-tertiary)",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: providerDotColor(provider),
                        }}
                      />
                      <span style={{ textTransform: "capitalize" }}>{provider}</span>
                    </div>
                    <div style={{ fontSize: "15px", fontWeight: 600, color: "var(--text-primary)" }}>
                      {formatCost(t.cost_usd)}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                      {t.requests} reqs
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Collapsible per-section breakdown — one section per mode in
              the fixed order Fast / Thinking / Pro / Deep Research /
              Judging / Audio / Other. Collapsed by default. */}
          <UsageSections sections={usageSections} />
        </>
      )}
    </div>
  );
}

// =============================================================================
// Usage Sub-Tab — shared month picker + admin-wide overview + user list
// =============================================================================
//
// One shared month picker drives BOTH the admin-wide overview AND the user
// list below it. The picker's month list comes from /admin/usage/summary;
// the per-user rows/expanders are scoped client-side off the same
// `selectedMonth`.

function AdminUsageTab() {
  const [data, setData] = useState<{ months: AdminUsageMonth[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>(ADMIN_ALL_TIME_KEY);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAdminUsageSummary()
      .then((d) => {
        if (!cancelled) {
          setData(d);
          // Default to the most recent month if available.
          const newest = d?.months?.[0]?.month || ADMIN_ALL_TIME_KEY;
          setSelectedMonth(newest);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Failed to load usage summary");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const months: AdminUsageMonth[] = data?.months || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* Shared month picker — drives the overview AND the user list.
          Labelled "Month" to match the Settings → Usage page picker. */}
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <label
          style={{
            fontSize: "11px",
            color: "var(--text-tertiary)",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.3px",
          }}
        >
          Month
        </label>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={adminMonthSelectStyle}
        >
          <option value={ADMIN_ALL_TIME_KEY}>{ADMIN_ALL_TIME_LABEL}</option>
          {months.map((m) => (
            <option key={m.month} value={m.month}>{m.month}</option>
          ))}
        </select>
      </div>

      {/* Admin-wide overview — totals / provider strip / per-model table */}
      <AdminUsageOverview
        data={data}
        loading={loading}
        error={error}
        selectedMonth={selectedMonth}
      />

      {/* User list — rows + expanders scoped to the same shared month */}
      <UsersSection selectedMonth={selectedMonth} />
    </div>
  );
}

// =============================================================================
// Alerts Sub-Tab — pricing proposals + model proposals
// =============================================================================

function AlertsSection({ onProposalChanged }: { onProposalChanged?: () => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* Pending pricing proposals (hidden when none) */}
      <PendingPricingProposals onChanged={onProposalChanged} />
      {/* Pending model-upgrade alerts (hidden when none) */}
      <PendingModelProposals onChanged={onProposalChanged} />
    </div>
  );
}

// =============================================================================
// Feedback Section (Exports content folded in at the top — wave 2)
// =============================================================================

function FeedbackSection() {
  // Raw (unfiltered) server data.
  const [rawEntries, setRawEntries] = useState<any[]>([]);
  const [rawExportEntries, setRawExportEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportsLoading, setExportsLoading] = useState(true);

  // Per-admin dismissed-IDs (server-synced via /admin/dismissed-ids).
  // Fetched independently — useMemo'd visible lists below re-filter
  // when either side updates, so the fetches can race freely.
  const [dismissedExportIds, setDismissedExportIds] = useState<Set<string>>(new Set());
  const [dismissedFeedbackIds, setDismissedFeedbackIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setLoading(true);
    fetchAdminFeedback()
      .then((data) => setRawEntries(data.feedback || []))
      .catch((e) => setError(e.message || "Failed to load feedback"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setExportsLoading(true);
    fetchAdminNotifications()
      .then((data) => {
        const exports = (data.notifications || []).filter(
          (n: { type?: string }) => n.type === "admin_export"
        );
        setRawExportEntries(exports);
      })
      .catch(() => setRawExportEntries([]))
      .finally(() => setExportsLoading(false));
  }, []);

  // Per-admin dismissed-IDs — populated once on mount. Sync to iOS +
  // any other client of this admin happens via /admin/dismissed-ids.
  useEffect(() => {
    fetchAdminDismissedIds()
      .then((data) => {
        setDismissedExportIds(new Set(data.exports || []));
        setDismissedFeedbackIds(new Set(data.feedback || []));
      })
      .catch(() => { /* soft-fail; user sees unfiltered view */ });
  }, []);

  // Visible (filtered) lists — recompute when either raw or dismissed
  // updates. This is what the render below iterates.
  const entries = useMemo(
    () => rawEntries.filter((e: { id: string }) => !dismissedFeedbackIds.has(e.id)),
    [rawEntries, dismissedFeedbackIds]
  );
  const exportEntries = useMemo(
    () => rawExportEntries.filter((e: { id: string }) => !dismissedExportIds.has(e.id)),
    [rawExportEntries, dismissedExportIds]
  );

  // Optimistic local update + fire-and-forget server POST. The dismissed
  // row(s) disappear immediately (visible lists recompute via useMemo);
  // the POST syncs to iOS + any other client. On POST failure the next
  // refresh resyncs from the server.
  const dismissExport = (id: string) => {
    setDismissedExportIds((prev) => new Set([...prev, id]));
    addAdminDismissedIds("exports", [id]).catch(() => { /* next refresh resyncs */ });
  };
  const clearExports = () => {
    const ids = exportEntries.map((e: { id: string }) => e.id);
    if (ids.length === 0) return;
    setDismissedExportIds((prev) => new Set([...prev, ...ids]));
    addAdminDismissedIds("exports", ids).catch(() => {});
  };
  const dismissFeedback = (id: string) => {
    setDismissedFeedbackIds((prev) => new Set([...prev, id]));
    addAdminDismissedIds("feedback", [id]).catch(() => {});
  };
  const clearFeedback = () => {
    const ids = entries.map((e: { id: string }) => e.id);
    if (ids.length === 0) return;
    setDismissedFeedbackIds((prev) => new Set([...prev, ...ids]));
    addAdminDismissedIds("feedback", ids).catch(() => {});
  };

  // Small "×" per-row dismiss button, shared by both row types.
  const dismissBtn = (onClick: () => void) => (
    <button
      onClick={onClick}
      aria-label="Dismiss"
      style={{
        flexShrink: 0,
        width: "20px",
        height: "20px",
        lineHeight: "16px",
        padding: 0,
        fontSize: "14px",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        background: "transparent",
        color: "var(--text-tertiary)",
        cursor: "pointer",
      }}
    >
      ×
    </button>
  );

  // Section header — bold label + an optional "Clear all" button.
  const sectionHeader = (label: string, showClear: boolean, onClear: () => void) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
      <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--text-primary)" }}>{label}</div>
      {showClear ? (
        <button
          onClick={onClear}
          style={{
            fontSize: "11px",
            padding: "2px 8px",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
            background: "transparent",
            color: "var(--text-secondary)",
            cursor: "pointer",
          }}
        >
          Clear all
        </button>
      ) : null}
    </div>
  );

  // ---- Feedback list body — load/error/empty/list ----
  let feedbackBody: React.ReactNode;
  if (loading) {
    feedbackBody = <div style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>Loading...</div>;
  } else if (error) {
    feedbackBody = <div style={{ color: "#e55", fontSize: "12px" }}>{error}</div>;
  } else if (entries.length === 0) {
    feedbackBody = (
      <div style={{ color: "var(--text-tertiary)", fontSize: "12px", padding: "12px 0", textAlign: "center" }}>
        No feedback yet.
      </div>
    );
  } else {
    feedbackBody = (
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {entries.map((entry: any) => (
          <div
            key={entry.id}
            style={{
              padding: "10px 12px",
              background: "var(--bg)",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", gap: "8px" }}>
              <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                {entry.name ? (
                  <>
                    <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-primary)" }}>
                      {entry.name}
                    </span>
                    <span style={{ fontSize: "11px", color: "var(--text-tertiary)" }}>
                      {entry.user_email}
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-primary)" }}>
                    {entry.user_email}
                  </span>
                )}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                <span style={{ fontSize: "11px", color: "var(--text-tertiary)", whiteSpace: "nowrap" }}>
                  {formatDate(entry.created_at)}
                </span>
                {dismissBtn(() => dismissFeedback(entry.id))}
              </span>
            </div>
            <div style={{ fontSize: "13px", color: "var(--text-secondary)", whiteSpace: "pre-wrap", lineHeight: "1.4" }}>
              {entry.message}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ---- Admin-exports body — one row per export (name, email, titles) ----
  let exportsBody: React.ReactNode;
  if (exportsLoading) {
    exportsBody = <div style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>Loading...</div>;
  } else if (exportEntries.length === 0) {
    exportsBody = (
      <div style={{ color: "var(--text-tertiary)", fontSize: "12px", padding: "8px 0" }}>
        No exports yet.
      </div>
    );
  } else {
    exportsBody = (
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {exportEntries.map((exp: any) => {
          const name = (exp.metadata?.user_name || "").trim();
          const titles: string[] = exp.metadata?.exported_titles || [];
          return (
            <div
              key={exp.id}
              style={{
                padding: "10px 12px",
                background: "var(--bg)",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", gap: "8px" }}>
                <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  {name ? (
                    <>
                      <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-primary)" }}>
                        {name}
                      </span>
                      <span style={{ fontSize: "11px", color: "var(--text-tertiary)" }}>
                        {exp.user_email}
                      </span>
                    </>
                  ) : (
                    <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-primary)" }}>
                      {exp.user_email}
                    </span>
                  )}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                  <span style={{ fontSize: "11px", color: "var(--text-tertiary)", whiteSpace: "nowrap" }}>
                    {formatDate(exp.created_at)}
                  </span>
                  {dismissBtn(() => dismissExport(exp.id))}
                </span>
              </div>
              <div style={{ fontSize: "13px", color: "var(--text-secondary)", lineHeight: "1.4" }}>
                {titles.length > 0 ? titles.join(", ") : "(untitled export)"}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* Admin exports from users — bold header + Clear all + per-export rows. */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {sectionHeader("Admin exports from users", exportEntries.length > 0, clearExports)}
        {exportsBody}
      </div>

      {/* Section break between Exports and Feedback */}
      <div style={{ height: "1px", background: "var(--border)" }} />

      {/* Feedback — bold header + Clear all + per-feedback rows. */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {sectionHeader("Feedback", entries.length > 0, clearFeedback)}
        {feedbackBody}
      </div>
    </div>
  );
}

// =============================================================================
// Notifications Banner
// =============================================================================

function NotificationsBanner() {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchAdminNotifications()
      .then((data) => {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      })
      .catch(() => {});
  }, []);

  if (dismissed || unreadCount === 0) return null;

  const unread = notifications.filter((n: any) => !n.read);

  const handleDismiss = async () => {
    try {
      await markAdminNotificationsRead();
      setDismissed(true);
    } catch {}
  };

  return (
    <div
      style={{
        padding: "10px 14px",
        background: "rgba(56, 139, 253, 0.12)",
        border: "1px solid rgba(56, 139, 253, 0.3)",
        borderRadius: "var(--radius-md)",
        fontSize: "12px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: unread.length > 0 ? "8px" : "0" }}>
        <span style={{ color: "#58a6ff", fontWeight: 600 }}>
          {unreadCount} new notification{unreadCount !== 1 ? "s" : ""}
        </span>
        <button
          onClick={handleDismiss}
          style={{
            background: "none",
            border: "none",
            color: "var(--text-tertiary)",
            fontSize: "11px",
            cursor: "pointer",
            padding: "2px 6px",
          }}
        >
          Dismiss
        </button>
      </div>
      {unread.map((n: any) => (
        <div
          key={n.id}
          style={{
            padding: "4px 0",
            color: "var(--text-secondary)",
            fontSize: "12px",
            borderTop: "1px solid rgba(56, 139, 253, 0.15)",
          }}
        >
          {n.message}
          <span style={{ color: "var(--text-tertiary)", marginLeft: "8px", fontSize: "11px" }}>
            {formatDate(n.created_at)}
          </span>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// AdminPanel
// =============================================================================

const adminSubTabs: { key: AdminSubTab; label: string }[] = [
  { key: "invites", label: "Invites" },
  { key: "feedback", label: "Feedback" },
  { key: "usage", label: "Usage" },
  { key: "pricing", label: "Pricing" },
  { key: "alerts", label: "Alerts" },
];

/** Server-side badge buckets passed to `onMarkTabRead`. The "usage"
 *  bucket (pending model/pricing updates) backs the "Alerts" tab. The
 *  "exports" bucket has no standalone tab — it is folded into "Feedback",
 *  so selecting Feedback marks both "feedback" and "exports" read. */
type BadgeBackedTab = "feedback" | "exports" | "usage";

interface AdminPanelProps {
  badgeCounts: AdminBadgeCounts;
  onMarkTabRead: (tab: BadgeBackedTab) => Promise<void>;
  /** Optional one-shot refresh hook after apply/reject; if absent, the 30s poll handles it. */
  onBadgeRefresh?: () => void;
}

export default function AdminPanel({ badgeCounts, onMarkTabRead, onBadgeRefresh }: AdminPanelProps) {
  const [subTab, setSubTab] = useState<AdminSubTab>("usage");

  const handleSelectSubTab = useCallback(
    (tab: AdminSubTab) => {
      setSubTab(tab);
      if (tab === "feedback") {
        // Exports is folded into the Feedback tab — selecting it clears
        // BOTH server badge buckets. Fire-and-forget — UI doesn't wait on
        // the server for the pill to disappear; the hook updates counts
        // optimistically.
        void onMarkTabRead("feedback");
        void onMarkTabRead("exports");
      } else if (tab === "alerts") {
        // The "alerts" tab's badge is the server-side "usage" bucket
        // (pending model/pricing updates) — mark that bucket read.
        void onMarkTabRead("usage");
      }
    },
    [onMarkTabRead]
  );

  const countForTab = (tab: AdminSubTab): number => {
    switch (tab) {
      // Exports is folded into Feedback — the Feedback pill shows the
      // combined feedback + exports badge count.
      case "feedback": return badgeCounts.feedback + badgeCounts.exports;
      // The "alerts" pill surfaces the server "usage" badge bucket
      // (pending model/pricing updates).
      case "alerts": return badgeCounts.usage;
      default: return 0;
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* Notifications */}
      <NotificationsBanner />

      {/* Sub-tab bar — sticky to the top of the scroll container
          (`.settings-panel`). The parent Settings tab bar lives OUTSIDE
          that scroll container (a flex sibling), so it never scrolls and
          this bar can park at top:0. Opaque background (var(--surface),
          the modal background — not the translucent var(--bg)) so scrolled
          content does not bleed through. z-index above content. */}
      <div
        style={{
          display: "flex",
          gap: "2px",
          background: "var(--surface)",
          borderRadius: "var(--radius-md)",
          padding: "2px",
          position: "sticky",
          top: 0,
          zIndex: 5,
        }}
      >
        {adminSubTabs.map((tab) => {
          const badgeCount = countForTab(tab.key);
          const showBadge = badgeCount > 0;
          return (
            <button
              key={tab.key}
              onClick={() => handleSelectSubTab(tab.key)}
              style={{
                flex: 1,
                padding: "5px 0",
                border: "none",
                borderRadius: "var(--radius-sm)",
                background: subTab === tab.key ? "var(--surface-hover)" : "transparent",
                color: subTab === tab.key ? "var(--text-primary)" : "var(--text-secondary)",
                fontSize: "11px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all var(--transition-fast)",
                position: "relative",
              }}
            >
              {tab.label}
              {showBadge && (
                <span
                  style={{
                    ...BADGE_PILL_STYLE,
                    marginLeft: "6px",
                    verticalAlign: "middle",
                  }}
                >
                  {formatBadgeCount(badgeCount)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Sub-tab content */}
      {subTab === "invites" && <InvitesSection />}
      {subTab === "feedback" && <FeedbackSection />}
      {subTab === "usage" && <AdminUsageTab />}
      {subTab === "pricing" && <PricingSection />}
      {subTab === "alerts" && <AlertsSection onProposalChanged={onBadgeRefresh} />}
    </div>
  );
}

// =============================================================================
// Pricing Sub-Tab — current rate table from /admin/pricing
// (its own standalone tab again — wave 2)
// =============================================================================

// Display name for a `display[]` row's `group`. Chat rows carry a chat
// mode as their group; the judging / other / audio sections carry their
// section name. Anything unrecognised falls back to a Title-cased value.
const PRICING_GROUP_LABEL: Record<string, string> = {
  fast: "Fast",
  thinking: "Thinking",
  pro: "Pro",
  deep_research: "Deep Research",
  judging: "Judging",
  other: "Other",
  audio: "Audio / TTS",
};

function pricingGroupLabel(group: string): string {
  return (
    PRICING_GROUP_LABEL[group] ||
    group.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function PricingSection() {
  const [data, setData] = useState<{
    display?: PricingDisplayRow[];
    rates: Record<string, Record<string, { input: number; output: number }>>;
    version: number;
    effective_from: string | null;
    using_fallback?: boolean;
    notes?: Record<string, string>;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAdminPricing()
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load pricing"));
  }, []);

  if (error) {
    return <div style={{ color: "var(--text-tertiary)", fontSize: 12 }}>Failed to load pricing: {error}</div>;
  }
  if (!data) {
    return <div style={{ color: "var(--text-tertiary)", fontSize: 12 }}>Loading…</div>;
  }

  // Render the server's pre-ordered `display[]` array in array order. The
  // server already sorts it mode-major (Anthropic/Gemini/OpenAI within
  // each chat mode), then judging, then other, then audio/TTS last.
  const display: PricingDisplayRow[] = data.display || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
        Current pricing table used for cost calculations. Version {data.version}
        {data.effective_from ? ` · effective ${data.effective_from.slice(0, 10)}` : ""}
        {data.using_fallback ? " · using fallback rates" : ""}.
      </div>

      {/* Same `admin-users-wide` wrapper the Usage tab's user table uses, so
          the Pricing sub-tab renders at the wide modal width (820px) to
          match the Usage sub-tab. */}
      <div className="admin-users-wide" style={{ overflowX: "auto" }}>
        {display.length === 0 ? (
          <div style={{ color: "var(--text-tertiary)", fontSize: 12, padding: "12px 0" }}>
            No pricing rows available.
          </div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Provider</th>
                <th style={thStyle}>Model</th>
                <th style={thRightStyle}>Input</th>
                <th style={thRightStyle}>Output</th>
              </tr>
            </thead>
            <tbody>
              {display.map((row, i) => {
                // A subheader row is emitted whenever `group` changes
                // from the preceding row — `display[]` is contiguous by
                // group, so a single boundary check suffices.
                const prevGroup = i > 0 ? display[i - 1].group : null;
                const showGroupHeader = row.group !== prevGroup;
                const unitLabel =
                  row.unit === "characters" ? "per M characters" : "per M tokens";
                return (
                  <Fragment key={`${row.group}|${row.model}|${i}`}>
                    {showGroupHeader && (
                      <tr>
                        <td
                          colSpan={4}
                          style={{
                            padding: "10px 8px 4px",
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            color: "var(--text-tertiary)",
                            borderBottom: "1px solid var(--border)",
                          }}
                        >
                          {pricingGroupLabel(row.group)}
                          <span
                            style={{
                              marginLeft: 8,
                              fontWeight: 500,
                              textTransform: "none",
                              letterSpacing: 0,
                              color: "var(--text-tertiary)",
                            }}
                          >
                            ({unitLabel})
                          </span>
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td style={tdStyle}>
                        <span style={{ color: providerDotColor(row.provider), fontWeight: 600 }}>●</span>{" "}
                        {row.provider}
                      </td>
                      <td style={tdStyle}>
                        {row.label}
                        {!row.priced && (
                          <span style={{ color: "var(--text-tertiary)", fontSize: 10, marginLeft: 4 }}>
                            (unpriced)
                          </span>
                        )}
                      </td>
                      <td style={tdRightStyle}>${Number(row.input).toFixed(2)}</td>
                      <td style={tdRightStyle}>${Number(row.output).toFixed(2)}</td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {data.notes && (
        <div style={{ fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.5 }}>
          {Object.values(data.notes).map((note, i) => (
            <p key={i} style={{ margin: "4px 0" }}>{note}</p>
          ))}
        </div>
      )}
    </div>
  );
}
